
import { useState, useMemo, useEffect } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Search, Wallet, Check, Filter } from "lucide-react"
import { useTransactions } from "@/lib/context/transaction-context"
import { useAccountSelection } from "@/lib/context/account-selection-context"
import { formatTransactionDate } from "@/lib/services/transactions"
import type { Transaction } from "@/lib/types"
import { TransactionDetail } from "@/components/transaction-detail"
import { NotificationBell } from "@/components/notification-bell"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

interface TransactionsProps {
  onNavigate?: (view: string) => void
}

export function Transactions({ onNavigate }: TransactionsProps = {}) {
  const { transactionsByMonth, totals, loading, refreshTransactions } = useTransactions()
  const { selectedAccountId, setSelectedAccountId, subAccounts, loading: loadingAccounts } = useAccountSelection()
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null)
  const [showTransactionDetail, setShowTransactionDetail] = useState(false)
  const [typeFilter, setTypeFilter] = useState<"all" | "income" | "expenses">("all")

  // Get all transactions flattened
  const allTransactions = Object.values(transactionsByMonth).flat()

  // Filter transactions by selected account
  const accountFilteredTransactions = useMemo(() => {
    if (selectedAccountId === "all") return allTransactions
    return allTransactions.filter(t => t.subAccountId === selectedAccountId)
  }, [allTransactions, selectedAccountId])

  // Filter transactions based on type, then search query
  const filteredTransactionsByMonth = useMemo(() => {
    // First, filter by type
    let typeFilteredTransactions = accountFilteredTransactions
    if (typeFilter === "income") {
      typeFilteredTransactions = accountFilteredTransactions.filter(t => t.amount > 0)
    } else if (typeFilter === "expenses") {
      typeFilteredTransactions = accountFilteredTransactions.filter(t => t.amount < 0)
    }

    // Group filtered transactions by month
    const transactionsByMonthFiltered = typeFilteredTransactions.reduce((acc, transaction) => {
      const monthKey = new Date(transaction.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
      if (!acc[monthKey]) acc[monthKey] = []
      acc[monthKey].push(transaction)
      return acc
    }, {} as Record<string, Transaction[]>)

    // Apply search filter
    return Object.entries(transactionsByMonthFiltered).reduce((acc, [month, transactions]) => {
      const filtered = transactions.filter(t =>
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
      if (filtered.length > 0) {
        acc[month] = filtered
      }
      return acc
    }, {} as Record<string, Transaction[]>)
  }, [accountFilteredTransactions, searchQuery, typeFilter])

  const handleTransactionClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction)
    setShowTransactionDetail(true)
  }

  const handleTransactionUpdate = async () => {
    // Reload transactions and totals after update
    await refreshTransactions()
  }

  // Keep selectedTransaction in sync with latest data from context
  // This ensures the detail view shows real-time updates after editing
  useEffect(() => {
    if (selectedTransaction && showTransactionDetail) {
      const updated = allTransactions.find(t => t.id === selectedTransaction.id)
      // Only update if the transaction data has actually changed
      if (updated && JSON.stringify(updated) !== JSON.stringify(selectedTransaction)) {
        setSelectedTransaction(updated)
      }
    }
  }, [allTransactions, selectedTransaction?.id, showTransactionDetail])

  const handleCloseDetail = () => {
    setShowTransactionDetail(false)
    setSelectedTransaction(null)
  }

  return (
    <div className="space-y-4 p-4">
      <div className="max-w-md mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Transactions</h1>
          <p className="text-sm text-muted-foreground mt-1">Track your expenses and income</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Account Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                <Wallet className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Filter by Account</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => setSelectedAccountId("all")}
                className="cursor-pointer"
              >
                <span>All Accounts</span>
                {selectedAccountId === "all" && <Check className="h-4 w-4 ml-auto" />}
              </DropdownMenuItem>
              {subAccounts.map((account) => (
                <DropdownMenuItem
                  key={account.id}
                  onClick={() => setSelectedAccountId(account.id)}
                  className="cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full" style={{ backgroundColor: account.color }} />
                    <span>{account.name}</span>
                  </div>
                  {selectedAccountId === account.id && <Check className="h-4 w-4 ml-auto" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <NotificationBell />
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        {/* Type Filter */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon">
              <Filter className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => setTypeFilter("all")}
              className="cursor-pointer"
            >
              <span>All</span>
              {typeFilter === "all" && <Check className="h-4 w-4 ml-auto" />}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setTypeFilter("income")}
              className="cursor-pointer"
            >
              <span>Income</span>
              {typeFilter === "income" && <Check className="h-4 w-4 ml-auto" />}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setTypeFilter("expenses")}
              className="cursor-pointer"
            >
              <span>Expenses</span>
              {typeFilter === "expenses" && <Check className="h-4 w-4 ml-auto" />}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Transactions List */}
      <div className="space-y-4">
        {loading ? (
          <Card className="p-4">
            <p className="text-center text-muted-foreground">Loading transactions...</p>
          </Card>
        ) : Object.keys(filteredTransactionsByMonth).length === 0 ? (
          <Card className="p-4">
            <p className="text-center text-muted-foreground">
              {searchQuery ? 'No transactions found' : 'No transactions yet'}
            </p>
          </Card>
        ) : (
          Object.entries(filteredTransactionsByMonth).map(([month, transactions]) => (
            <div key={month} className="space-y-2">
              <h3 className="text-sm font-semibold text-muted-foreground">{month}</h3>
              {transactions.map((transaction) => (
                <Card
                  key={transaction.id}
                  className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => handleTransactionClick(transaction)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`h-12 w-12 rounded-xl ${transaction.color} flex items-center justify-center text-xl flex-shrink-0`}
                    >
                      {transaction.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{transaction.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {transaction.category} • {formatTransactionDate(transaction.date)}
                      </p>
                    </div>
                    <div className={`text-base font-semibold ${transaction.amount > 0 ? "text-income" : "text-expense"}`}>
                      {transaction.amount > 0 ? "+" : "-"}${Math.abs(transaction.amount).toFixed(2)}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ))
        )}
      </div>

      {/* Transaction Detail Dialog */}
      <TransactionDetail
        transaction={selectedTransaction}
        open={showTransactionDetail}
        onClose={handleCloseDetail}
        onUpdate={handleTransactionUpdate}
      />
      </div>
    </div>
  )
}
